'use client';

import { useState, useEffect } from 'react';
import {
  TextField,
  Box,
  Typography,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  InputAdornment,
} from '@mui/material';
import { Check, Close, Visibility, VisibilityOff } from '@mui/icons-material';

interface PasswordRequirement {
  label: string;
  test: (password: string) => boolean;
}

const passwordRequirements: PasswordRequirement[] = [
  {
    label: 'At least 8 characters',
    test: password => password.length >= 8,
  },
  {
    label: 'Contains a number',
    test: password => /\d/.test(password),
  },
  {
    label: 'Contains a lowercase letter',
    test: password => /[a-z]/.test(password),
  },
  {
    label: 'Contains an uppercase letter',
    test: password => /[A-Z]/.test(password),
  },
];

interface PasswordFieldProps {
  value?: string;
  onChange?: (value: string) => void;
  onValidationChange?: (isValid: boolean) => void;
  disabled?: boolean;
  autoComplete?: string;
  showConfirmation?: boolean;
  label?: string;
  confirmLabel?: string;
  showRequirementsValidation?: boolean;
  error?: boolean;
  helperText?: string;
  defaultValue?: string;
  name?: string;
}

export default function PasswordField({
  onChange,
  onValidationChange,
  disabled = false,
  autoComplete = 'new-password',
  showConfirmation = false,
  label = 'Password',
  confirmLabel = 'Confirm Password',
  showRequirementsValidation = false,
  error,
  helperText,
  defaultValue,
  name,
}: PasswordFieldProps) {
  const [showRequirements, setShowRequirements] = useState(false);
  const [strength, setStrength] = useState(0);
  const [confirmation, setConfirmation] = useState('');
  const [showValidation, setShowValidation] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmationPassword, setShowConfirmationPassword] = useState(false);
  const [value, setValue] = useState(defaultValue || '');

  useEffect(() => {
    if (value.length > 0 && !showValidation && showRequirementsValidation) {
      setShowValidation(true);
    } else if (value.length === 0) {
      setShowValidation(false);
    }

    // Calculate password strength
    const meetsRequirements = passwordRequirements.filter(req => req.test(value));
    const newStrength = (meetsRequirements.length / passwordRequirements.length) * 100;
    setStrength(newStrength);

    // Check if password is valid (meets all requirements and matches confirmation if required)
    const isValid = newStrength === 100 && (!showConfirmation || value === confirmation);
    onValidationChange?.(isValid);
  }, [
    value,
    confirmation,
    showConfirmation,
    showValidation,
    onValidationChange,
    showRequirementsValidation,
  ]);

  const getStrengthColor = (strength: number) => {
    if (strength < 30) return 'error';
    if (strength < 70) return 'warning';
    return 'success';
  };

  const passwordsMatch = !showConfirmation || value === confirmation;

  const handleClickShowPassword = () => setShowPassword(show => !show);
  const handleClickShowConfirmationPassword = () => setShowConfirmationPassword(show => !show);

  const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setValue(event.target.value);
    onChange?.(event.target.value);
  };

  return (
    <Box>
      <TextField
        fullWidth
        required
        name={name || 'password'}
        label={label}
        type={showPassword ? 'text' : 'password'}
        autoComplete={autoComplete}
        defaultValue={defaultValue}
        onChange={handleChange}
        onFocus={() => showRequirementsValidation && setShowRequirements(true)}
        disabled={disabled}
        error={error}
        helperText={helperText}
        slotProps={{
          input: {
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle password visibility"
                  onClick={handleClickShowPassword}
                  onMouseDown={handleMouseDownPassword}
                  edge="end"
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          },
        }}
      />

      {/* Strength indicator */}
      {showValidation && showRequirementsValidation && (
        <Box sx={{ mt: 1, mb: 2 }}>
          <LinearProgress
            variant="determinate"
            value={strength}
            color={getStrengthColor(strength)}
            sx={{ height: 8, borderRadius: 4 }}
          />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
            <Typography variant="caption" color={getStrengthColor(strength)}>
              Password Strength: {strength === 100 ? 'Strong' : strength > 60 ? 'Medium' : 'Weak'}
            </Typography>
            {showRequirements && (
              <Typography
                variant="caption"
                sx={{ cursor: 'pointer', textDecoration: 'underline' }}
                onClick={() => setShowRequirements(!showRequirements)}
              >
                {showRequirements ? 'Hide Requirements' : 'Show Requirements'}
              </Typography>
            )}
          </Box>
        </Box>
      )}

      {/* Requirements list */}
      {showValidation && showRequirements && showRequirementsValidation && (
        <List dense sx={{ mb: 2 }}>
          {passwordRequirements.map((req, index) => (
            <ListItem key={index} dense>
              <ListItemIcon sx={{ minWidth: 36 }}>
                {req.test(value) ? (
                  <Check color="success" fontSize="small" />
                ) : (
                  <Close color="error" fontSize="small" />
                )}
              </ListItemIcon>
              <ListItemText
                primary={req.label}
                primaryTypographyProps={{
                  variant: 'caption',
                  color: req.test(value) ? 'success.main' : 'error.main',
                }}
              />
            </ListItem>
          ))}
        </List>
      )}

      {showConfirmation && (
        <TextField
          fullWidth
          required
          name="passwordConfirmation"
          label={confirmLabel}
          type={showConfirmationPassword ? 'text' : 'password'}
          autoComplete="new-password"
          value={confirmation}
          onChange={e => setConfirmation(e.target.value)}
          disabled={disabled}
          error={confirmation.length > 0 && !passwordsMatch}
          helperText={confirmation.length > 0 && !passwordsMatch ? 'Passwords do not match' : ' '}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle password visibility"
                  onClick={handleClickShowConfirmationPassword}
                  onMouseDown={handleMouseDownPassword}
                  edge="end"
                >
                  {showConfirmationPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      )}
    </Box>
  );
}
